"use client"

import { useState, useMemo, useEffect, useCallback } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBox, faCheckCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons"
import DashboardStats from "../../components/inventory/sections/DashboardStats"
import Card from "../../components/inventory/modules/Card/Card"
import Button from "../../components/inventory/modules/Button/Button"
import SearchBar from "../../components/shared-modules/SearchBar"
import Select from "../../components/inventory/modules/Select/Select"
import Table from "../../components/inventory/modules/table/table"
import Tabs from "../../components/inventory/modules/Tabs/Tabs"
import OrderInventory from "../../components/inventory/order"
import OrderDetailModal from "../../components/inventory/modal/OrderDetailModal"
import Badge from "../../components/inventory/modules/Badge/Badge"
import Spinner from "../../components/spinner"
import DropshipRequestsSection from "../../components/inventory/sections/DropshipRequestsSection"
import {
  getClientBulkOrders,
  getTenoviBulkOrders,
  getClientDropshipOrders,
  getLocationList,
  getDeviceTypes,
  fetchByUrl,
} from "../../services/inventory/detail"
import { getWarehouseStats } from "../../services/inventory/devices"
import styles from "../../styles/inventory/orders-page.module.css"
import { parseDateSafe } from "../../utils/dateFormatters"

const transformClientOrders = (rawOrders = []) =>
  rawOrders.map((order) => {
    const totalDevices = order.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0
    const deviceTypes = order.items?.map((i) => i.device_type_name) || []
    const deviceTypeDisplay = deviceTypes.length === 1 ? deviceTypes[0] : "Multiple"
    return {
      id: order.client_bulkorderid || order.id,
      orderNumber: order.order_number,
      location: order.location_name,
      locationId: order.location_id,
      orderDate: order.ordered_at ? parseDateSafe(order.ordered_at)?.toLocaleDateString() || "" : "",
      status: order.status,
      devices: totalDevices,
      deviceType: deviceTypeDisplay,
      orderedBy: order.ordered_by_name,
      tenantName: order.tenant_name,
      canceledAt: order.canceled_at,
      deliveredAt: order.delivered_at,
      tenant: order.tenant,
      cancelledBy: order.cancelled_by,
      originalOrder: order,
    }
  })

const transformTenoviOrders = (rawOrders = []) =>
  rawOrders.map((order) => {
    const totalDevices = order.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0
    const deviceTypes = [...new Set(order.items?.map((i) => i.device_type_name) || [])]
    const deviceTypeDisplay = deviceTypes.length === 1 ? deviceTypes[0] : "Multiple"
    return {
      id: order.id,
      orderNumber: order.order_number,
      location: "Warehouse",
      orderDate: order.ordered_at ? parseDateSafe(order.ordered_at)?.toLocaleDateString() || "" : "",
      status: order.status,
      devices: totalDevices,
      deviceType: deviceTypeDisplay,
      orderedBy: order.ordered_by_name,
      canceledAt: order.canceled_at,
      deliveredAt: order.delivered_at,
      originalOrder: order,
    }
  })

const transformDropshipOrders = (rawOrders = []) =>
  rawOrders.map((order) => ({
    id: order.id,
    orderNumber: order.order_number || `DS-${order.id}`,
    location: order.location_name,
    orderDate: order.created_at ? parseDateSafe(order.created_at)?.toLocaleDateString() || "" : "",
    status: order.status,
    devices: order.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 1,
    deviceType: order.name || "Unknown",
    orderedBy: order.requested_by_name || "",
    originalOrder: order,
  }))

function useDebounced(value, delay = 500) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

const getActiveClientId = () => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null
    const storedAcc = localStorage.getItem("inventory_active_account")
    if (!storedAcc) return null
    const account = JSON.parse(storedAcc)

    if (account?.role === "Admin") {
      return null
    }
    return account?.id || null
  } catch (err) {
    return null
  }
}

const OrdersPage = ({ currentUser }) => {
  const [isAdmin, setIsAdmin] = useState(false)
  const [locations, setLocations] = useState([])

  const [apiLoading, setApiLoading] = useState(false)
  const [paginationLoading, setPaginationLoading] = useState(false)

  const [clientOrdersData, setClientOrdersData] = useState({ results: [], meta: {} })
  const [tenoviOrdersData, setTenoviOrdersData] = useState({ results: [], meta: {} })

  const [activeClientTab, setActiveClientTab] = useState("client-orders")
  const [isOrderInventoryModalOpen, setIsOrderInventoryModalOpen] = useState(false)
  const [orderDetailModal, setOrderDetailModal] = useState({ isOpen: false, selectedOrder: null, editMode: false })

  const [globalLocationFilter, setGlobalLocationFilter] = useState("all")
  const [deviceTypeFilter, setDeviceTypeFilter] = useState("all")

  const [clientOrdersSearchTerm, setClientOrdersSearchTerm] = useState("")
  const [clientOrdersStatusFilter, setClientOrdersStatusFilter] = useState("all")

  const [dropshipSearchTerm, setDropshipSearchTerm] = useState("")
  const [dropshipStatusFilter, setDropshipStatusFilter] = useState("all")

  const [tenoviOrdersSearchTerm, setTenoviOrdersSearchTerm] = useState("")
  const [tenoviOrdersLocationFilter, setTenoviOrdersLocationFilter] = useState("all")
  const [tenoviOrdersStatusFilter, setTenoviOrdersStatusFilter] = useState("all")

  const [deviceTypes, setDeviceTypes] = useState([])

  // hold raw warehouse stats response from API (new behaviour: API provides ready stats)
  const [warehouseStatsResponse, setWarehouseStatsResponse] = useState(null)

  // KPI stats passed to DashboardStats (driven by tab + admin/tenant)
  const [kpiStats, setKpiStats] = useState(null)

  const debouncedClientOrdersSearch = useDebounced(clientOrdersSearchTerm, 500)
  const debouncedTenoviOrdersSearch = useDebounced(tenoviOrdersSearchTerm, 500)
  const debouncedDropshipSearch = useDebounced(dropshipSearchTerm, 500)

  const checkAdminRole = useCallback(() => {
    try {
      const inventoryActiveAccount = localStorage.getItem("inventory_active_account")
      if (inventoryActiveAccount) {
        const account = JSON.parse(inventoryActiveAccount)
        const isAdminFlag = account.type === "admin" || account.type === ""
        setIsAdmin(isAdminFlag)
        return isAdminFlag
      } else {
        setIsAdmin(true)
        return true
      }
    } catch (error) {
      console.error("Error reading inventory_active_account from sessionStorage:", error)
      setIsAdmin(true)
      return true
    }
  }, [])

  const locationOptions = useMemo(() => {
    const baseOptions = [{ value: "all", label: "All Locations" }]
    if (locations && locations.length > 0) {
      const dynamicOptions = locations.map((location) => {
        const label = location.name || location.state || location.city || "Unnamed Location"
        return { value: location.id, label }
      })
      return [...baseOptions, ...dynamicOptions]
    }
    return baseOptions
  }, [locations])

  const fetchPageByUrl = useCallback(async (url, orderType) => {
    if (!url) {
      setPaginationLoading(false)
      return
    }
    try {
      const json = await fetchByUrl(url)
      if (!json) {
        setPaginationLoading(false)
        return
      }

      const results = Array.isArray(json.results)
        ? json.results
        : Array.isArray(json.data)
        ? json.data
        : Array.isArray(json)
        ? json
        : []

      const meta = json.meta || (json.data && json.data.meta) || {}

      if (orderType === "client") {
        setClientOrdersData({ results: transformClientOrders(results), meta: meta || {} })
      } else if (orderType === "tenovi") {
        setTenoviOrdersData({ results: transformTenoviOrders(results), meta: meta || {} })
      }

      setPaginationLoading(false)
    } catch (err) {
      console.error("Error fetching pagination URL:", err)
      setPaginationLoading(false)
    }
  }, [])

  const fetchClientOrders = useCallback(async (urlOrFilters = {}) => {
    setPaginationLoading(true)
    try {
      let response
      if (typeof urlOrFilters === "string") {
        response = await fetchByUrl(urlOrFilters)
      } else {
        response = await getClientBulkOrders(urlOrFilters)
      }

      const data = response?.success ? response.data : response
      const results = data?.results || data || []
      const transformed = transformClientOrders(results)
      setClientOrdersData({ results: transformed, meta: data?.meta || {} })
    } catch (error) {
      console.error("Error fetching client orders:", error)
      setClientOrdersData({ results: [], meta: {} })
    } finally {
      setPaginationLoading(false)
    }
  }, [])

  const fetchTenoviOrders = useCallback(async (urlOrFilters = {}) => {
    setPaginationLoading(true)
    try {
      let response
      if (typeof urlOrFilters === "string") {
        response = await fetchByUrl(urlOrFilters)
      } else {
        response = await getTenoviBulkOrders(urlOrFilters)
      }

      const data = response?.success ? response.data : response
      const results = data?.results || data || []
      const transformed = transformTenoviOrders(results)
      setTenoviOrdersData({ results: transformed, meta: data?.meta || {} })
    } catch (error) {
      console.error("Error fetching tenovi orders:", error)
      setTenoviOrdersData({ results: [], meta: {} })
    } finally {
      setPaginationLoading(false)
    }
  }, [])

  const handleOrderPageChange = useCallback(
    (pageUrlOrNumber, orderType) => {
      setPaginationLoading(true)
      if (typeof pageUrlOrNumber === "string") {
        fetchPageByUrl(pageUrlOrNumber, orderType)
      } else if (typeof pageUrlOrNumber === "number") {
        const meta = orderType === "client" ? clientOrdersData.meta : tenoviOrdersData.meta
        const baseLink = (meta && (meta.first || meta.base_url || meta.base)) || null
        if (!baseLink) {
          setPaginationLoading(false)
          return
        }
        const url = `${baseLink}${baseLink.includes("?") ? "&" : "?"}page=${pageUrlOrNumber}`
        fetchPageByUrl(url, orderType)
      }
    },
    [fetchPageByUrl, clientOrdersData.meta, tenoviOrdersData.meta],
  )

  // --- NEW: sort state for client & tenovi tables
  const [clientSort, setClientSort] = useState({ sort_by: null, order: null })
  const [tenoviSort, setTenoviSort] = useState({ sort_by: null, order: null })

  const buildClientParams = useCallback(() => {
    const params = {}
    if (debouncedClientOrdersSearch && debouncedClientOrdersSearch.trim() !== "") {
      params.order_number = debouncedClientOrdersSearch.trim()
    }
    if (clientOrdersStatusFilter && clientOrdersStatusFilter !== "all") {
      params.status = clientOrdersStatusFilter
    }
    if (globalLocationFilter && globalLocationFilter !== "all") {
      params.location_id = globalLocationFilter
    }
    if (deviceTypeFilter && deviceTypeFilter !== "all") {
      params.device_type = deviceTypeFilter
    }

    // include sorting params when set
    if (clientSort?.sort_by) params.sort_by = clientSort.sort_by
    if (clientSort?.order) params.order = clientSort.order

    return params
  }, [debouncedClientOrdersSearch, clientOrdersStatusFilter, globalLocationFilter, deviceTypeFilter, clientSort])

  const buildTenoviParams = useCallback(() => {
    const params = {}
    if (debouncedTenoviOrdersSearch && debouncedTenoviOrdersSearch.trim() !== "") {
      params.order_number = debouncedTenoviOrdersSearch.trim()
    }
    if (tenoviOrdersStatusFilter && tenoviOrdersStatusFilter !== "all") {
      params.status = tenoviOrdersStatusFilter
    }
    if (tenoviOrdersLocationFilter && tenoviOrdersLocationFilter !== "all") {
      params.location_id = tenoviOrdersLocationFilter
    }
    if (deviceTypeFilter && deviceTypeFilter !== "all") {
      params.device_type = deviceTypeFilter
    }

    // include sorting params when set
    if (tenoviSort?.sort_by) params.sort_by = tenoviSort.sort_by
    if (tenoviSort?.order) params.order = tenoviSort.order

    return params
  }, [debouncedTenoviOrdersSearch, tenoviOrdersStatusFilter, tenoviOrdersLocationFilter, deviceTypeFilter, tenoviSort])

  useEffect(() => {
    const params = buildClientParams()
    fetchClientOrders(params)
  }, [buildClientParams, fetchClientOrders])

  useEffect(() => {
    const params = buildTenoviParams()
    fetchTenoviOrders(params)
  }, [buildTenoviParams, fetchTenoviOrders])

  const buildBulkKpis = (src = {}) => {
    return [
      { title: "Pending", value: Number(src.pending ?? 0), changeType: "neutral", iconColor: "#3b82f6", icon: <FontAwesomeIcon icon={faBox} /> },
      { title: "Delivered", value: Number(src.delivered ?? src.shipped ?? 0), changeType: "positive", iconColor: "#06b6d4", icon: <FontAwesomeIcon icon={faCheckCircle} /> },
      { title: "Cancelled", value: Number(src.cancelled ?? 0), changeType: "negative", iconColor: "#ef4444", icon: <FontAwesomeIcon icon={faTimesCircle} /> },
      { title: "Total", value: Number(src.total ?? 0), changeType: "neutral", iconColor: "#374151", icon: <FontAwesomeIcon icon={faBox} /> },
    ]
  }

  const buildDropshipKpis = (src = {}) => {
    return [
      { title: "Pending", value: Number(src.pending ?? 0), changeType: "neutral", iconColor: "#3b82f6", icon: <FontAwesomeIcon icon={faBox} /> },
      { title: "Dropship Requested", value: Number(src.dropship_requested ?? src.dropshipRequested ?? 0), changeType: "neutral", iconColor: "#3b82f6", icon: <FontAwesomeIcon icon={faBox} /> },
      { title: "Shipped", value: Number(src.shipped ?? 0), changeType: "positive", iconColor: "#06b6d4", icon: <FontAwesomeIcon icon={faCheckCircle} /> },
      { title: "Cancelled", value: Number(src.cancelled ?? 0), changeType: "negative", iconColor: "#ef4444", icon: <FontAwesomeIcon icon={faTimesCircle} /> },
    ]
  }

  // recompute the KPI cards whenever the raw warehouse response, admin flag or active tab changes
  useEffect(() => {
    // if API hasn't returned yet, keep kpiStats null
    if (!warehouseStatsResponse) return

    try {
      if (isAdmin) {
        // warehouse-level stats come from API directly
        const overall = warehouseStatsResponse.overall_order_stats || {}
        if (activeClientTab === "dropship-requests") {
          setKpiStats(buildDropshipKpis(overall.dropship || {}))
        } else {
          // default: bulk
          setKpiStats(buildBulkKpis(overall.bulk || {}))
        }
      } else {
        // non-admin: show tenant-specific cards (find tenant in tenants_stats)
        const tenants = warehouseStatsResponse.tenants_stats || []
        const activeClientId = getActiveClientId()
        const tenant = tenants.find((t) => String(t.tenant_id || t.tenant || "").toLowerCase() === String(activeClientId || "").toLowerCase())

        if (tenant) {
          if (activeClientTab === "dropship-requests") {
            setKpiStats(buildDropshipKpis(tenant.dropship_orders || {}))
          } else {
            setKpiStats(buildBulkKpis(tenant.bulk_orders || {}))
          }
        } else {
          // fallback zeros
          setKpiStats(activeClientTab === "dropship-requests" ? buildDropshipKpis({}) : buildBulkKpis({}))
        }
      }
    } catch (err) {
      console.error("Error computing KPI cards:", err)
    }
  }, [warehouseStatsResponse, isAdmin, activeClientTab])

  useEffect(() => {
    const initializeData = async () => {
      setApiLoading(true)
      const isUserAdmin = checkAdminRole()

      await Promise.all([fetchClientOrders(), fetchTenoviOrders()])

      if (!isUserAdmin) {
        try {
          const locationResponse = await getLocationList()
          if (locationResponse?.success) setLocations(locationResponse.data || [])
        } catch (error) {
          console.error("Error fetching locations:", error)
        }
      }

      try {
        // Fetch warehouse stats (API now returns ready-to-display stats)
        const statsResponse = await getWarehouseStats()
        if (statsResponse) setWarehouseStatsResponse(statsResponse)
      } catch (err) {
        console.error("Error fetching warehouse stats for KPIs:", err)
        setWarehouseStatsResponse(null)
      } finally {
        setApiLoading(false)
      }
    }
    initializeData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const fetchDeviceTypes = async () => {
      try {
        const data = await getDeviceTypes()
        if (data && Array.isArray(data)) {
          const options = [
            { value: "all", label: "All Device Types" },
            ...data.map((d) => ({ value: d.id, label: d.name })),
          ]
          setDeviceTypes(options)
        } else {
          setDeviceTypes([{ value: "all", label: "All Device Types" }])
        }
      } catch (error) {
        console.error("Error fetching device types:", error)
        setDeviceTypes([{ value: "all", label: "All Device Types" }])
      }
    }
    fetchDeviceTypes()
  }, [])

  useEffect(() => {
    const params = {}
    if (clientOrdersStatusFilter && clientOrdersStatusFilter !== "all") params.status = clientOrdersStatusFilter
    if (globalLocationFilter && globalLocationFilter !== "all") params.location_id = globalLocationFilter
    if (deviceTypeFilter && deviceTypeFilter !== "all") params.device_type = deviceTypeFilter

    if (debouncedClientOrdersSearch && debouncedClientOrdersSearch.trim() !== "") params.order_number = debouncedClientOrdersSearch.trim()
    fetchClientOrders(params)
  }, [globalLocationFilter, clientOrdersStatusFilter, deviceTypeFilter, debouncedClientOrdersSearch, fetchClientOrders])

  useEffect(() => {
    const params = {}
    if (tenoviOrdersStatusFilter && tenoviOrdersStatusFilter !== "all") params.status = tenoviOrdersStatusFilter
    if (tenoviOrdersLocationFilter && tenoviOrdersLocationFilter !== "all") params.location_id = tenoviOrdersLocationFilter
    if (deviceTypeFilter && deviceTypeFilter !== "all") params.device_type = deviceTypeFilter
    if (debouncedTenoviOrdersSearch && debouncedTenoviOrdersSearch.trim() !== "") params.order_number = debouncedTenoviOrdersSearch.trim()
    fetchTenoviOrders(params)
  }, [tenoviOrdersLocationFilter, tenoviOrdersStatusFilter, deviceTypeFilter, debouncedTenoviOrdersSearch, fetchTenoviOrders])

  const clientCount = clientOrdersData.results.length
  const combinedClientTotal = clientCount

  const mergedClientAndDropshipForStats = useMemo(() => {
    return [...clientOrdersData.results]
  }, [clientOrdersData.results])

  const orderStatusOptions = [
    { value: "all", label: "All Statuses" },
    { value: "pending", label: "Pending", color: "#fef3c7", text: "#d97706", dotColor: "#d97706" },
    { value: "processing", label: "Processing", color: "#dbeafe", text: "#1e40af", dotColor: "#1e40af" },
    { value: "shipped", label: "Shipped", color: "#dcfce7", text: "#16a34a", dotColor: "#16a34a" },
    { value: "delivered", label: "Delivered", color: "#d1fae5", text: "#065f46", dotColor: "#065f46" },
    { value: "cancelled", label: "Cancelled", color: "#fee2e2", text: "#dc2626", dotColor: "#dc2626" },
  ]

  const dropshipStatusOptions = [
    { value: "all", label: "All Statuses" },
    { value: "pending", label: "Pending" },
    { value: "dropship requested", label: "Dropship Requested", color: "#dbeafe", text: "#1e40af", dotColor: "#1e40af" },
    { value: "shipped", label: "Shipped" },
    { value: "delivered", label: "Delivered" },
    { value: "cancelled", label: "Cancelled" },
  ]

  const defaultDeviceTypeOptions = [{ value: "all", label: "All Device Types" }]

  const handleStatusUpdateFromModal = (newStatus, updatedOrder) => {
    if (!updatedOrder) return
    const id = updatedOrder.id
    setClientOrdersData((prev) => ({
      ...prev,
      results: prev.results.map((o) => (o.id === id ? { ...o, status: newStatus } : o)),
    }))
    setTenoviOrdersData((prev) => ({
      ...prev,
      results: prev.results.map((o) => (o.id === id ? { ...o, status: newStatus } : o)),
    }))
  }

  function renderStatusBadge(order) {
    const status = (order.status || "").toString().toLowerCase()
    let variant = "default"
    switch (status) {
      case "pending":
        variant = "warning"
        break
      case "shipped":
        variant = "success"
        break
      case "delivered":
        variant = "success"
        break
      case "cancelled":
        variant = "danger"
        break
      default:
        variant = "outline"
    }
    return (
      <Badge variant={variant} size="medium" dot={true}>
        {order.status}
      </Badge>
    )
  }

const renderOrNA = (row, ...keys) => {
  for (const k of keys) {
    if (row == null) break
    const v = row[k]
    if (v !== undefined && v !== null && String(v) !== "") return v
  }
  return "N/A"
}

const clientOrderColumns = [
  {
    key: "orderNumber",
    label: "Order #",
    alignLeft: true,
    sorting: true,
    sortField: "order_number",
    render: (row) => renderOrNA(row, "orderNumber", "order_number", "orderNumberDisplay", "order_no"),
  },
  {
    key: "orderedBy",
    label: "Ordered By",
    alignLeft: true,
    sorting: false,
    render: (row) => renderOrNA(row, "orderedBy", "ordered_by", "customer_name", "tenant_name"),
  },
  {
    key: "location",
    label: "Location",
    alignLeft: true,
    sorting: true,
    sortField: "location_name",
    render: (row) => renderOrNA(row, "location", "location_name", "location_name_display"),
  },
  {
    key: "orderDate",
    label: "Date",
    alignLeft: true,
    sorting: true,
    sortField: "ordered_at",
    render: (row) => {
      const v = renderOrNA(row, "orderDate", "ordered_at", "created_at")
      // if it's a date-like string and not "N/A", you can format here; otherwise return v
      return v === "N/A" ? v : v
    },
  },
  {
    key: "delivered_at",
    label: "Delivered at",
    alignLeft: true,
    sorting: true,
    sortField: "delivered_at",
    render: (row) => renderOrNA(row, "delivered_at", "deliveredAt"),
  },
  // {
  //   key: "delivered_by",
  //   label: "Delivered By",
  //   alignLeft: true,
  //   sorting: true,
  //   sortField: "delivered_by",
  //   render: (row) => renderOrNA(row, "delivered_by", "deliveredBy"),
  // },
  {
    key: "status",
    label: "Status",
    sorting: true,
    render: renderStatusBadge,
  },
  {
    key: "devices",
    label: "Devices",
    alignLeft: true,
    sorting: true,
    sortField: "devices",
    render: (row) => {
      // prefer a count, then a list, then fallback
      if (!row) return "N/A"
      if (Array.isArray(row.devices) && row.devices.length >= 0) return row.devices.length === 0 ? "0" : row.devices.length
      if (typeof row.devices === "number") return String(row.devices)
      if (row.devices_summary) return row.devices_summary
      return renderOrNA(row, "devices", "device_count", "deviceCount")
    },
  },
  {
    key: "deviceType",
    label: "Device Type",
    alignLeft: true,
    sorting: true,
    sortField: "device_type",
    render: (row) =>
      renderOrNA(
        row,
        "deviceType",
        "device_type",
        "device_type_name",
        "device.device_type?.name" /* note: keep simple fields in backend objects */
      ),
  },
]


  const tenoviOrderColumns = [
    { key: "orderNumber", label: "Order #", alignLeft: true, sorting: true, sortField: "order_number" },
    { key: "orderedBy", label: "Ordered By", alignLeft: true, sorting: false },
    { key: "orderDate", label: "Date", alignLeft: true, sorting: true, sortField: "ordered_at" },
    { key: "status", label: "Status", sorting: true, render: renderStatusBadge },
    { key: "deviceType", label: "Device Type", alignLeft: true, sorting: true, sortField: "device_type" },
    { key: "devices", label: "Total Devices", alignLeft: true, sorting: true, sortField: "devices" },
  ]

  const handleNewOrderClick = () => setIsOrderInventoryModalOpen(true)
  const handleOrderInventoryModalClose = () => setIsOrderInventoryModalOpen(false)
  const handleRowClick = (order, editMode = false) => setOrderDetailModal({ isOpen: true, selectedOrder: order, editMode })
  const handleOrderDetailModalClose = () => setOrderDetailModal({ isOpen: false, selectedOrder: null, editMode: false })

  return (
    <div className={styles.orders_page}>
      <div className={styles.page_header}>
        <div className={styles.header_left}>
          <h1 className={styles.page_title}>Orders</h1>
          <p className={styles.page_subtitle}>Track and manage device orders across all warehouses</p>
        </div>
        <div className={styles.header_buttons}>
          <Button variant="primary" size="small" onClick={handleNewOrderClick}>
            New Order +
          </Button>
        </div>
      </div>

      <div className={styles.section}>
        <Card
          title={`Orders from Client`}
          headerRight={
            <div className={styles.card_header_with_filters}>
              <div className={styles.filters_group}>
                {!isAdmin && (
                  <Select
                    options={locationOptions}
                    value={globalLocationFilter}
                    onChange={setGlobalLocationFilter}
                    placeholder="Filter by Location"
                    size="small"
                    width="200px"
                  />
                )}
                <Select
                  options={deviceTypes.length ? deviceTypes : defaultDeviceTypeOptions}
                  value={deviceTypeFilter}
                  onChange={setDeviceTypeFilter}
                  placeholder="Filter by Device Type"
                  size="small"
                  width="220px"
                />
              </div>
            </div>
          }
        >
          <div className={styles.section}>
            {apiLoading ? <Spinner /> : <DashboardStats stats={kpiStats || []} />}
          </div>

          <div className={styles.tabs_container}>
            <Tabs
              tabs={[{ id: "client-orders", label: `Bulk Orders ` }, { id: "dropship-requests", label: `Dropship Requests` }]}
              activeTab={activeClientTab}
              onChange={setActiveClientTab}
              variant="secondary"
              size="medium"
            />
          </div>

          {activeClientTab === "client-orders" && (
            <>
              <div className={styles.card_header_with_filters_2}>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <SearchBar searchTerm={clientOrdersSearchTerm} onSearchChange={setClientOrdersSearchTerm} placeholder="Search orders..." width="300px" />
                </div>
                <div className={styles.filters_group}>
                  <Select
                    options={orderStatusOptions}
                    value={clientOrdersStatusFilter}
                    onChange={setClientOrdersStatusFilter}
                    placeholder="Filter by Status"
                    size="small"
                    width="150px"
                  />
                </div>
              </div>

              <div>
                <div className={styles.table_container}>
                  <Table
                    columns={clientOrderColumns}
                    data={clientOrdersData}
                    height={500}
                    loading={apiLoading}
                    pagination={true}
                    meta={clientOrdersData.meta}
                    paginationLoading={paginationLoading}
                    onPageChange={(pageUrl) => handleOrderPageChange(pageUrl, "client")}
                    isRowClickable={true}
                    onRowClick={(order) => handleRowClick(order, false)}
                    onSortChange={(sort_by, order) => {
                      setClientSort({ sort_by: sort_by ?? null, order: order ?? null })
                      if (clientOrdersData?.meta) {
                        fetchClientOrders({ ...buildClientParams(), page: 1 })
                      } else {
                        fetchClientOrders({ ...buildClientParams() })
                      }
                    }}
                  />
                </div>
              </div>
            </>
          )}

          {activeClientTab === "dropship-requests" && (
            <div>
              <div className={styles.card_header_with_filters_2}>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <SearchBar searchTerm={dropshipSearchTerm} onSearchChange={setDropshipSearchTerm} placeholder="Search dropship requests..." width="300px" />
                </div>
                <div className={styles.filters_group}>
                  <Select
                    options={dropshipStatusOptions}
                    value={dropshipStatusFilter}
                    onChange={setDropshipStatusFilter}
                    placeholder="Filter by Dropship Status"
                    size="small"
                    width="200px"
                  />
                </div>
              </div>

              <div className={styles.section}>
                <DropshipRequestsSection
                  selectedLocation={globalLocationFilter}
                  searchTerm={debouncedDropshipSearch}
                  statusFilter={dropshipStatusFilter}
                  deviceTypeFilter={deviceTypeFilter}
                />
              </div>
            </div>
          )}
        </Card>
      </div>

      <div className={styles.section}>
        <Card title={`Orders to Tenovi  (${tenoviOrdersData.meta.total_items})`}>
          <div className={styles.card_header_with_filters_2}>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <SearchBar searchTerm={tenoviOrdersSearchTerm} onSearchChange={setTenoviOrdersSearchTerm} placeholder="Search Tenovi orders..." width="300px" />
            </div>
            <div className={styles.filters_group}>
              <Select
                options={orderStatusOptions}
                value={tenoviOrdersStatusFilter}
                onChange={setTenoviOrdersStatusFilter}
                placeholder="Filter by Status"
                size="small"
                width="150px"
              />
            </div>
          </div>

          <div className={styles.table_container}>
            <Table
              columns={tenoviOrderColumns}
              data={tenoviOrdersData}
              height={700}
              pagination={true}
              meta={tenoviOrdersData.meta}
              paginationLoading={paginationLoading}
              onPageChange={(pageUrl) => handleOrderPageChange(pageUrl, "tenovi")}
              isRowClickable={true}
              onRowClick={(order) => handleRowClick(order, false)}
              loading={apiLoading}
              onSortChange={(sort_by, order) => {
                setTenoviSort({ sort_by: sort_by ?? null, order: order ?? null })
                if (tenoviOrdersData?.meta) {
                  fetchTenoviOrders({ ...buildTenoviParams(), page: 1 })
                } else {
                  fetchTenoviOrders({ ...buildTenoviParams() })
                }
              }}
            />
          </div>
        </Card>
      </div>

      {isOrderInventoryModalOpen && <OrderInventory isOpen={isOrderInventoryModalOpen} setIsOpen={handleOrderInventoryModalClose} />}

      {orderDetailModal.isOpen && orderDetailModal.selectedOrder && (
        <OrderDetailModal
          isOpen={orderDetailModal.isOpen}
          onClose={handleOrderDetailModalClose}
          order={orderDetailModal.selectedOrder}
          editMode={orderDetailModal.editMode}
          onStatusChange={handleStatusUpdateFromModal}
        />
      )}
    </div>
  )
}

export default OrdersPage
